import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Target, Copy, Check } from 'lucide-react';
import CustomDropdown from '@/components/common-components/filter options/CustomDropdown';
import CustomDatePicker from '@/components/common-components/filter options/CustomDataPicker';
import { useCampaignAPI } from '@/hooks/campaigns/useCampaignAPI';
import { useAuth } from '@/hooks/authentication/useAuth';
import { isValidEmail } from '@/lib/validation';

const CampaignDetailsForm = forwardRef(({
    campaignData,
    setCampaignData,
    onSaveDraft,
    selectedRegion,
    selectedBrand
}, ref) => {
    const { user } = useAuth();
    const { 
        campaignOptions, 
        loading: optionsLoading, 
        getCampaignOptions,
        getCampaigns,
        getCampaignById,
        getAmplifyCampaignName,
        loading: campaignsLoading
    } = useCampaignAPI();
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState('new'); // 'new' or 'existing'
    const [existingCampaigns, setExistingCampaigns] = useState([]);
    const [selectedCampaignId, setSelectedCampaignId] = useState('');
    const [amplifyCampaignName, setAmplifyCampaignName] = useState('');
    const [loadingAmplifyName, setLoadingAmplifyName] = useState(false);
    const [copiedAmplifyName, setCopiedAmplifyName] = useState(false);

    useEffect(() => {
        getCampaignOptions();
    }, [getCampaignOptions]);

    // Pre-fill BDR Email from logged-in user when empty (new campaign only)
    const loginEmail = user?.email || user?.user_email;
    useEffect(() => {
        if (loginEmail && !campaignData.bdrEmail?.trim() && !campaignData.isEditMode) {
            setCampaignData(prev => ({ ...prev, bdrEmail: loginEmail }));
        }
    }, [loginEmail, campaignData.bdrEmail, campaignData.isEditMode]);

    // Fetch Amplify campaign name when campaign name and budget type change
    useEffect(() => {
        const fetchAmplifyName = async () => {
            // Only fetch if both campaign name and budget type are provided
            if (!campaignData.campaignName || campaignData.campaignName.trim().length < 3 || !campaignData.budget_type) {
                setAmplifyCampaignName('');
                return;
            }

            try {
                setLoadingAmplifyName(true);
                const result = await getAmplifyCampaignName(
                    selectedRegion,
                    selectedBrand,
                    campaignData.campaignName,
                    campaignData.budget_type
                );
                
                // console.log('Amplify campaign name result:', result);
                
                // The API returns the data object directly (not wrapped in status/data)
                if (result && result.amplify_campaign_name) {
                    setAmplifyCampaignName(result.amplify_campaign_name);
                } else {
                    setAmplifyCampaignName('');
                }
            } catch (error) {
                console.error('Error fetching amplify campaign name:', error);
                setAmplifyCampaignName('');
            } finally {
                setLoadingAmplifyName(false);
            }
        };

        // Debounce the API call
        const timeoutId = setTimeout(fetchAmplifyName, 500);
        return () => clearTimeout(timeoutId);
    }, [campaignData.campaignName, campaignData.budget_type, selectedRegion, selectedBrand, getAmplifyCampaignName]);

    // Fetch existing campaigns when tab changes to 'existing'
    useEffect(() => {
        if (activeTab === 'existing' && selectedRegion && selectedBrand) {
            loadExistingCampaigns();
        }
    }, [activeTab, selectedRegion, selectedBrand]);

    const loadExistingCampaigns = async () => {
        try {
            const campaigns = await getCampaigns(selectedRegion, selectedBrand);
            setExistingCampaigns(campaigns || []);
        } catch (error) {
            console.error('Error loading campaigns:', error);
            alert('Failed to load existing campaigns');
        }
    };

    const handleExistingCampaignSelect = async (campaignId) => {
        if (!campaignId) {
            setSelectedCampaignId('');
            return;
        }

        try {
            setSelectedCampaignId(campaignId);
            const campaignDetails = await getCampaignById(campaignId);
            
            // Map API response to form data structure
            setCampaignData({
                campaignId: campaignDetails.id,
                campaignName: campaignDetails.campaign_name,
                platform: campaignDetails.campaign_type,
                campaignObjective: campaignDetails.campaign_objective,
                totalBudget: campaignDetails.total_budget,
                startDate: campaignDetails.start_date,
                endDate: campaignDetails.end_date,
                territory: campaignDetails.territory || '',
                noOfPocs: campaignDetails.no_of_pocs,
                gl_code: campaignDetails.gl_code,
                cost_centre: campaignDetails.cost_center,
                budget_type: campaignDetails.budget_type,
                bdrEmail: campaignDetails.bdr_email,
                role: campaignDetails.role,
                region: campaignDetails.region,
                brand: campaignDetails.brand,
                isEditMode: true // Flag to indicate we're editing an existing campaign
            });
        } catch (error) {
            console.error('Error loading campaign details:', error);
            alert('Failed to load campaign details');
        }
    };

    const handleInputChange = (field, value) => {
        setCampaignData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleCopyAmplifyName = () => {
        if (amplifyCampaignName) {
            navigator.clipboard.writeText(amplifyCampaignName).then(() => {
                setCopiedAmplifyName(true);
                setTimeout(() => setCopiedAmplifyName(false), 2000);
            }).catch(err => {
                console.error('Failed to copy:', err);
            });
        }
    };

    //   console.log("Campaign Options in CampaignDetailsForm: ", campaignOptions);
    const validateForm = () => {
        const newErrors = {};

        if (!campaignData.campaignName?.trim()) {
            newErrors.campaignName = 'Campaign name is required';
        }

        if (!campaignData.platform) {
            newErrors.platform = 'Campaign type is required';
        }

        if (!campaignData.campaignObjective) {
            newErrors.campaignObjective = 'Objective is required';
        }

        if (!campaignData.totalBudget || campaignData.totalBudget <= 0) {
            newErrors.totalBudget = 'Valid total budget is required';
        }

        if (!campaignData.startDate) {
            newErrors.startDate = 'Start date is required';
        }

        if (!campaignData.endDate) {
            newErrors.endDate = 'End date is required';
        }

        if (campaignData.startDate && campaignData.endDate &&
            new Date(campaignData.startDate) >= new Date(campaignData.endDate)) {
            newErrors.endDate = 'End date must be after start date';
        }

        if (!campaignData.gl_code?.trim()) {
            newErrors.gl_code = 'GL Code is required';
        }

        if (!campaignData.cost_centre?.trim()) {
            newErrors.cost_centre = 'Cost Centre is required';
        }

        if (!campaignData.budget_type) {
            newErrors.budget_type = 'Budget Type is required';
        }

        if (!campaignData.bdrEmail?.trim()) {
            newErrors.bdrEmail = 'BDR Email is required';
        } else if (!isValidEmail(campaignData.bdrEmail)) {
            newErrors.bdrEmail = 'Please enter a valid email address';
        }

        if (!campaignData.role) {
            newErrors.role = 'Role is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Expose validate method to parent component
    useImperativeHandle(ref, () => ({
        validate: () => {
            return validateForm();
        }
    }));

    const isFormValid = () => {
        return campaignData.campaignName?.trim() &&
            campaignData.platform &&
            campaignData.campaignObjective &&
            campaignData.totalBudget && campaignData.totalBudget > 0 &&
            campaignData.startDate &&
            campaignData.endDate &&
            campaignData.startDate < campaignData.endDate &&
            campaignData.gl_code?.trim() &&
            campaignData.cost_centre?.trim() &&
            campaignData.budget_type &&
            campaignData.bdrEmail?.trim() &&
            isValidEmail(campaignData.bdrEmail) &&
            campaignData.role;
    };

    return (
        <div className="max-w-5xl mx-auto">
            <Card className="border-0 shadow-sm">
                <CardContent className="p-8">
                    {/* Campaign Details Header */}
                    <div className="mb-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                <Target className="w-5 h-5 text-yellow-500" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">Campaign Details</h2>
                                <p className="text-sm text-gray-500">Define the core parameters of your request.</p>
                            </div>
                        </div>
                    </div>

                    {/* Tab Selection */}
                    <div className="flex gap-4 mb-6 border-b">
                        <button
                            type="button"
                            onClick={() => {
                                setActiveTab('new');
                                setSelectedCampaignId('');
                            }}
                            className={`px-4 py-2 font-medium transition-colors ${
                                activeTab === 'new'
                                    ? 'text-blue-600 border-b-2 border-blue-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            New Campaign
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('existing')}
                            className={`px-4 py-2 font-medium transition-colors ${
                                activeTab === 'existing'
                                    ? 'text-blue-600 border-b-2 border-blue-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Edit Existing Campaign
                        </button>
                    </div>

                    {/* Existing Campaign Selector */}
                    {activeTab === 'existing' && (
                        <div className="mb-6">
                            <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                Select Campaign to Edit <span className="text-red-500">*</span>
                            </Label>
                            <CustomDropdown
                                options={existingCampaigns.map(camp => ({
                                    value: camp.campaign_id.toString(),
                                    label: camp.campaign_name
                                }))}
                                value={selectedCampaignId}
                                onChange={handleExistingCampaignSelect}
                                placeholder="Select a campaign..."
                                loading={campaignsLoading}
                            />
                            {existingCampaigns.length === 0 && !campaignsLoading && (
                                <p className="text-sm text-gray-500 mt-2">
                                    No existing campaigns found for {selectedRegion} - {selectedBrand}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Show form only if new campaign OR existing campaign is selected */}
                    {(activeTab === 'new' || (activeTab === 'existing' && selectedCampaignId)) && (
                    <div className="space-y-6">
                        {/* Campaign Name */}
                        <div className="space-y-2">
                            <Label htmlFor="campaignName" className="text-sm font-medium text-gray-700">
                                Campaign Name <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="campaignName"
                                placeholder="e.g., Corona Sunsets 2"
                                value={campaignData.campaignName || ''}
                                onChange={(e) => handleInputChange('campaignName', e.target.value)}
                                className={`${errors.campaignName ? 'border-red-500' : ''}`}
                            />
                            {errors.campaignName && (
                                <p className="text-xs text-red-500">{errors.campaignName}</p>
                            )}
                        </div>

                        {/* Campaign Type and Objective Row */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="platform" className="text-sm font-medium text-gray-700">
                                    Campaign Type <span className="text-red-500">*</span>
                                </Label>
                                <CustomDropdown
                                    options={campaignOptions?.campaign_type || []}
                                    value={campaignData.platform}
                                    onChange={(value) => handleInputChange('platform', value)}
                                    // placeholder="LTO/PTC"
                                    className={`${errors.platform ? 'border-red-500' : ''}`}
                                />
                                {errors.platform && (
                                    <p className="text-xs text-red-500">{errors.platform}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="objective" className="text-sm font-medium text-gray-700">
                                    Objective <span className="text-red-500">*</span>
                                </Label>
                                <CustomDropdown
                                    options={campaignOptions?.objective || []}
                                    value={campaignData.campaignObjective}
                                    onChange={(value) => handleInputChange('campaignObjective', value)}
                                    // placeholder="Traffic"
                                    className={`${errors.campaignObjective ? 'border-red-500' : ''}`}
                                />
                                {errors.campaignObjective && (
                                    <p className="text-xs text-red-500">{errors.campaignObjective}</p>
                                )}
                            </div>
                        </div>

                        {/* Total Budget, Start Date, End Date Row */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="totalBudget" className="text-sm font-medium text-gray-700">
                                    Total Budget ($) <span className="text-red-500">*</span>
                                </Label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                    <Input
                                        id="totalBudget"
                                        type="number"
                                        // placeholder="3,000"
                                        value={campaignData.totalBudget || ''}
                                        onChange={(e) => handleInputChange('totalBudget', e.target.value)}
                                        className={`pl-7 ${errors.totalBudget ? 'border-red-500' : ''}`}
                                    />
                                </div>
                                {errors.totalBudget && (
                                    <p className="text-xs text-red-500">{errors.totalBudget}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <div className="space-y-2">
                                    <CustomDatePicker
                                        label="Start Date"
                                        defaultValue={campaignData.startDate ? new Date(campaignData.startDate) : null}
                                        onDateChange={(date) => handleInputChange('startDate', date ? date.toISOString().split('T')[0] : '')}
                                        required={true}
                                        minDate={new Date()}
                                    />
                                    {errors.startDate && (
                                        <p className="text-sm text-red-500">{errors.startDate}</p>
                                    )}
                                </div>
                            </div>
                            {/* End Date */}
                            <div className="space-y-2">
                                <CustomDatePicker
                                    label="End Date"
                                    defaultValue={campaignData.endDate ? new Date(campaignData.endDate) : null}
                                    onDateChange={(date) => handleInputChange('endDate', date ? date.toISOString().split('T')[0] : '')}
                                    required={true}
                                    minDate={campaignData.startDate ? new Date(campaignData.startDate) : new Date()}
                                />
                                {errors.endDate && (
                                    <p className="text-sm text-red-500">{errors.endDate}</p>
                                )}
                            </div>
                        </div>

                        {/* GL Code and Cost Centre Row */}
                        <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="glCode" className="text-sm font-medium text-gray-700">
                                    GL Code <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="glCode"
                                    placeholder="Enter GL Code"
                                    value={campaignData.gl_code || ''}
                                    onChange={(e) => handleInputChange('gl_code', e.target.value)}
                                    className={`${errors.gl_code ? 'border-red-500' : ''}`}
                                />
                                {errors.gl_code && (
                                    <p className="text-xs text-red-500">{errors.gl_code}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="costCentre" className="text-sm font-medium text-gray-700">
                                    Cost Centre <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="costCentre"
                                    placeholder="Enter Cost Centre"
                                    value={campaignData.cost_centre || ''}
                                    onChange={(e) => handleInputChange('cost_centre', e.target.value)}
                                    className={`${errors.cost_centre ? 'border-red-500' : ''}`}
                                />
                                {errors.cost_centre && (
                                    <p className="text-xs text-red-500">{errors.cost_centre}</p>
                                )}
                            </div>
                             <div className="space-y-2">
                                <Label htmlFor="budgetType" className="text-sm font-medium text-gray-700">
                                    Budget Type <span className="text-red-500">*</span>
                                </Label>
                                <CustomDropdown
                                    options={campaignOptions?.budget_type || []}
                                    value={campaignData.budget_type}
                                    onChange={(value) => handleInputChange('budget_type', value)}
                                    placeholder="Select Budget Type"
                                    loading={optionsLoading}
                                    className={`${errors.budget_type ? 'border-red-500' : ''}`}
                                />
                                {errors.budget_type && (
                                    <p className="text-xs text-red-500">{errors.budget_type}</p>
                                )}
                            </div>
                        </div>

                        {/* Budget Type */}
                        {/* <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="budgetType" className="text-sm font-medium text-gray-700">
                                    Budget Type <span className="text-red-500">*</span>
                                </Label>
                                <CustomDropdown
                                    options={campaignOptions?.budget_type || []}
                                    value={campaignData.budget_type}
                                    onChange={(value) => handleInputChange('budget_type', value)}
                                    placeholder="Select Budget Type"
                                    loading={optionsLoading}
                                    className={`${errors.budget_type ? 'border-red-500' : ''}`}
                                />
                                {errors.budget_type && (
                                    <p className="text-xs text-red-500">{errors.budget_type}</p>
                                )}
                            </div>
                        </div> */}
                        <div className="grid grid-cols-2 gap-4">
                            {/* BDR Email (read-only, from logged-in user) */}
                            <div className="space-y-2 text-gray-700">
                                <Label htmlFor="bdrEmail" className="text-sm font-medium text-gray-700">BDR Email <span className="text-red-500">*</span></Label>
                                <Input
                                    id="bdrEmail"
                                    type="email"
                                    placeholder="From your account"
                                    value={campaignData.bdrEmail || ''}
                                    readOnly
                                    disabled
                                    className={`bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed opacity-90 ${errors.bdrEmail ? 'border-red-500' : ''}`}
                                />
                                {errors.bdrEmail && (
                                    <p className="text-sm text-red-500">{errors.bdrEmail}</p>
                                )}
                            </div>
                            {/* Role */}
                            <div className="space-y-2 text-gray-700">
                                <Label htmlFor="role" className="text-sm font-medium text-gray-700">Role <span className="text-red-500">*</span></Label>
                                <CustomDropdown
                                    options={campaignOptions?.role || []}
                                    placeholder="Select role"
                                    value={campaignData.role || ''}
                                    onChange={(value) => handleInputChange('role', value)}
                                    loading={optionsLoading}
                                    className={errors.role ? 'border-red-500' : ''}
                                />
                                {errors.role && (
                                    <p className="text-sm text-red-500">{errors.role}</p>
                                )}
                            </div>
                        </div>

                        {/* Amplify Campaign Name Display - At the end of form */}
                        {campaignData.campaignName && campaignData.budget_type && (
                            <div className="mt-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl shadow-sm">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Target className="w-5 h-5 text-blue-600" />
                                            <h3 className="text-sm font-bold text-blue-900 uppercase tracking-wide">
                                                Amplify Campaign Name
                                            </h3>
                                        </div>
                                        <p className="text-xs text-gray-600 mb-3">
                                            This is the name that will be used in the Amplify platform. Please copy this for your records.
                                        </p>
                                        
                                        {loadingAmplifyName ? (
                                            <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-blue-200">
                                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                                <span className="text-sm text-blue-600 font-medium">Generating campaign name...</span>
                                            </div>
                                        ) : amplifyCampaignName ? (
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 p-3 bg-white rounded-lg border-2 border-blue-400">
                                                    <p className="text-lg font-bold text-gray-900 break-all">
                                                        {amplifyCampaignName}
                                                    </p>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={handleCopyAmplifyName}
                                                    className={`flex items-center gap-2 px-4 py-2 h-auto ${
                                                        copiedAmplifyName 
                                                            ? 'bg-green-100 border-green-500 text-green-700' 
                                                            : 'bg-white border-blue-400 text-blue-700 hover:bg-blue-50'
                                                    }`}
                                                >
                                                    {copiedAmplifyName ? (
                                                        <>
                                                            <Check className="w-4 h-4" />
                                                            Copied!
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Copy className="w-4 h-4" />
                                                            Copy
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-300">
                                                <p className="text-sm text-yellow-800 italic">
                                                    Enter both Campaign Name and Budget Type to generate Amplify campaign name
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
});

export default CampaignDetailsForm;
